import { useEffect, useState, ChangeEvent } from 'react'
import { Tenant } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Building2, MapPin, Mail, X } from 'lucide-react'

export interface TenantFormValues {
  name: string
  tenant_type: 'PF' | 'PJ'
  email?: string
  phone?: string

  // PF Fields
  cpf?: string
  full_name?: string
  birth_date?: string

  // PJ Fields
  cnpj?: string
  company_name?: string
  trade_name?: string
  creci?: string

  // Address
  zip_code?: string
  street?: string
  number?: string
  complement?: string
  neighborhood?: string
  city?: string
  state?: string
  address?: string // Fallback/Full address
  capabilities_override?: any // JSON string or object
}

interface TenantManagementDialogProps {
  tenant: Tenant | null
  open: boolean
  submitting: boolean
  onClose: () => void
  onSubmit: (values: any) => Promise<void> | void
}

export function TenantManagementDialog({
  tenant,
  open,
  submitting,
  onClose,
  onSubmit
}: TenantManagementDialogProps) {
  const [formState, setFormState] = useState<TenantFormValues>({
    name: '',
    tenant_type: 'PJ'
  })

  useEffect(() => {
    if (tenant) {
      setFormState({
        name: tenant.name,
        tenant_type: (tenant.tenant_type as 'PF' | 'PJ') ?? 'PJ',
        email: tenant.email ?? '',
        phone: tenant.phone ?? '',

        cpf: tenant.cpf ?? '',
        full_name: tenant.full_name ?? '',
        birth_date: tenant.birth_date ? new Date(tenant.birth_date).toISOString().split('T')[0] : '',

        cnpj: tenant.cnpj ?? '',
        company_name: tenant.company_name ?? '',
        trade_name: tenant.trade_name ?? '',
        creci: tenant.creci ?? '',

        zip_code: tenant.zip_code ?? '',
        street: tenant.street ?? '',
        number: tenant.number ?? '',
        complement: tenant.complement ?? '',
        neighborhood: tenant.neighborhood ?? '',
        city: tenant.city ?? '',
        state: tenant.state ?? '',
        address: tenant.address ?? '',
        capabilities_override: tenant.capabilities_override ? JSON.stringify(tenant.capabilities_override, null, 2) : ''
      })
    }
  }, [tenant, open])

  if (!open || !tenant) return null

  const isPJ = formState.tenant_type === 'PJ'
  const isPF = formState.tenant_type === 'PF'

  const handleChange = (field: keyof TenantFormValues) => (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let value = event.target.value
    if (field === 'state') value = value.toUpperCase()
    setFormState(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    // Validate JSON
    if (typeof formState.capabilities_override === 'string' && formState.capabilities_override) {
      try {
        JSON.parse(formState.capabilities_override)
      } catch (e) {
        alert('JSON de Capabilities Override inválido')
        return
      }
    }

    // Convert string back to object for submission if needed, 
    // but TenantFormValues defines it as string? No, API likely expects object.
    // Let's check apiService.updateTenant signature next.
    // For now assuming onSubmit handles it or we modify formState before passing.

    // Actually, let's keep it simple: pass the formState as is, but we might need to cast/transform in parent.
    // Wait, the parent `handleEditSubmit` calls `apiService.updateTenant`.
    // We should check if `apiService.updateTenant` expects `Tenant` object or `TenantFormValues`.
    // The definition of `TenantFormValues` has `capabilities_override` as string now.
    // We should probably transform it here or in the parent.
    // Let's modify the parent onSubmit to expect the parsed object? 
    // Or just pass the parsed object in a new constructed object.

    const submissionData: any = { ...formState }
    if (typeof submissionData.capabilities_override === 'string' && submissionData.capabilities_override) {
      try {
        submissionData.capabilities_override = JSON.parse(submissionData.capabilities_override)
      } catch (e) {
        // Already validated above
      }
    } else {
      submissionData.capabilities_override = null
    }

    await onSubmit(submissionData)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50/50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Editar Tenant</h2>
            <p className="text-sm text-gray-500">Atualize as informações cadastrais de {tenant.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-8">

          {/* Identificação Básica */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 uppercase tracking-wider">
                <Building2 className="w-4 h-4 text-blue-600" />
                Identificação
              </h3>

              <div className="space-y-3">
                <div className="grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Nome de Exibição / Fantasia *</label>
                  <Input
                    value={formState.name}
                    onChange={handleChange('name')}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <label className="text-sm font-medium text-gray-700">Tipo *</label>
                    <select
                      value={formState.tenant_type}
                      onChange={handleChange('tenant_type')}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="PJ">Pessoa Jurídica</option>
                      <option value="PF">Pessoa Física</option>
                    </select>
                  </div>
                  <div className="grid gap-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      {isPJ ? 'CNPJ *' : 'CPF *'}
                    </label>
                    <Input
                      value={isPJ ? formState.cnpj : formState.cpf}
                      onChange={handleChange(isPJ ? 'cnpj' : 'cpf')}
                      placeholder={isPJ ? '00.000.000/0000-00' : '000.000.000-00'}
                      required
                    />
                  </div>
                </div>

                {isPJ && (
                  <div className="grid gap-1.5">
                    <label className="text-sm font-medium text-gray-700">Razão Social</label>
                    <Input
                      value={formState.company_name}
                      onChange={handleChange('company_name')}
                    />
                  </div>
                )}

                {isPF && (
                  <div className="grid gap-1.5">
                    <label className="text-sm font-medium text-gray-700">Nome Completo</label>
                    <Input
                      value={formState.full_name}
                      onChange={handleChange('full_name')}
                    />
                  </div>
                )}

                {isPJ && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1.5">
                      <label className="text-sm font-medium text-gray-700">CRECI</label>
                      <Input
                        value={formState.creci}
                        onChange={handleChange('creci')}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contato */}
            <div className="space-y-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 uppercase tracking-wider">
                <Mail className="w-4 h-4 text-blue-600" />
                Contato
              </h3>
              <div className="space-y-3">
                <div className="grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Email Principal</label>
                  <Input
                    type="email"
                    value={formState.email}
                    onChange={handleChange('email')}
                  />
                </div>
                <div className="grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Telefone</label>
                  <Input
                    value={formState.phone}
                    onChange={handleChange('phone')}
                  />
                </div>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Endereço */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 uppercase tracking-wider">
              <MapPin className="w-4 h-4 text-blue-600" />
              Endereço
            </h3>

            <div className="grid gap-3">
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-2 grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">CEP</label>
                  <Input
                    value={formState.zip_code}
                    onChange={handleChange('zip_code')}
                  />
                </div>
                <div className="col-span-3 grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Cidade</label>
                  <Input
                    value={formState.city}
                    onChange={handleChange('city')}
                  />
                </div>
                <div className="col-span-1 grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">UF</label>
                  <Input
                    value={formState.state}
                    onChange={handleChange('state')}
                    maxLength={2}
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-3 grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Logradouro</label>
                  <Input
                    value={formState.street}
                    onChange={handleChange('street')}
                  />
                </div>
                <div className="col-span-1 grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Número</label>
                  <Input
                    value={formState.number}
                    onChange={handleChange('number')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Bairro</label>
                  <Input
                    value={formState.neighborhood}
                    onChange={handleChange('neighborhood')}
                  />
                </div>
                <div className="grid gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Complemento</label>
                  <Input
                    value={formState.complement}
                    onChange={handleChange('complement')}
                  />
                </div>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Configurações Avançadas */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 uppercase tracking-wider">
              <Building2 className="w-4 h-4 text-blue-600" />
              Configurações Avançadas
            </h3>
            <div className="space-y-3">
              <div className="grid gap-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Capabilities Override (JSON)
                </label>
                <textarea
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                  value={formState.capabilities_override || ''}
                  onChange={(e) => setFormState(prev => ({ ...prev, capabilities_override: e.target.value }))}
                  placeholder='{ "gamification": true, "max_users": 50 }'
                />
                <p className="text-xs text-gray-500">
                  Sobrescreve as configurações do plano. Use com cuidado. Deve ser um JSON válido.
                </p>
              </div>
            </div>
          </div>

        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50/50">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} className="min-w-[120px]">
            {submitting ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </div>
    </div>
  )
}
